import React, { useState, useEffect } from 'react';
import { profileService } from '../../../services/profileService';
import AddressModal from '../../../component/profile/AddressModal';

const Addresses = () => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    try {
      setLoading(true);
      const response = await profileService.getAddresses();
      if (response.success) {
        setAddresses(response.addresses || []);
      } else {
        setMessage({ type: 'error', text: response.message });
      }
    } catch (error) {
      console.error('Failed to fetch addresses:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to load addresses' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (address = null) => {
    if (address) {
      setEditingId(address._id);
      setModalData({
        ...address,
        phone: address.phone || '+234 000 000 0000'
      });
    } else {
      setEditingId(null);
      setModalData({
        fullName: '',
        phone: '+234 000 000 0000',
        state: 'FCT',
        city: 'Municipal Area Council',
        postalCode: '900001',
        addressLine1: '',
        addressLine2: '',
        isDefault: false,
        country: 'Nigeria'
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
    setModalData(null);
  };

  const handleSubmitAddress = async (formData) => {
    try {
      setMessage({ type: '', text: '' });
      
      if (editingId) {
        const response = await profileService.updateAddress(editingId, formData);
        if (response.success) {
          setMessage({ type: 'success', text: 'Address updated successfully!' });
        } else {
          throw new Error(response.message);
        }
      } else {
        const response = await profileService.addAddress(formData);
        if (response.success) {
          setMessage({ type: 'success', text: 'Address added successfully!' });
        } else {
          throw new Error(response.message);
        }
      }
      
      fetchAddresses();
      handleCloseModal();
    } catch (error) {
      console.error('Failed to save address:', error);
      setMessage({ type: 'error', text: error.message });
      throw error;
    }
  };

  const handleDelete = async (addressId) => {
    if (window.confirm('Are you sure you want to delete this address?')) {
      try {
        setMessage({ type: '', text: '' });
        const response = await profileService.deleteAddress(addressId);
        if (response.success) {
          setMessage({ type: 'success', text: 'Address deleted successfully!' });
          fetchAddresses();
        } else {
          setMessage({ type: 'error', text: response.message });
        }
      } catch (error) {
        console.error('Failed to delete address:', error);
        setMessage({ type: 'error', text: error.message });
      }
    }
  };

  const handleSetDefault = async (addressId) => {
    try {
      setMessage({ type: '', text: '' });
      const response = await profileService.setDefaultAddress(addressId);
      if (response.success) {
        setMessage({ type: 'success', text: 'Default address updated!' });
        fetchAddresses();
      } else {
        setMessage({ type: 'error', text: response.message });
      }
    } catch (error) {
      console.error('Failed to set default address:', error);
      setMessage({ type: 'error', text: error.message });
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4A574]"></div>
      </div>
    );
  }

  return (
    <div className="px-2 sm:px-0">
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Saved Addresses</h1>
      <p className="text-sm sm:text-base text-[#6B635D] mb-6 sm:mb-8">
        Add or update delivery addresses for your orders and future gifts.
      </p>

      {message.text && (
        <div
          className={`mb-4 sm:mb-6 p-3 sm:p-4 text-sm ${
            message.type === 'success'
              ? 'bg-green-50 text-green-700'
              : 'bg-red-50 text-red-700'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Addresses List */}
      {addresses.length === 0 ? (
        <div className="text-left">
          <h3 className="text-base sm:text-lg font-medium text-secondary mb-2">No Saved Address Yet</h3>
          <p className="text-sm sm:text-base text-[#6B635D] mb-4">Add your first address to speed up checkout.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {addresses.map((address) => (
            <div
              key={address._id}
              className="border border-[#DBDBDB] p-3 sm:p-4 transition-colors"
            >
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="font-medium text-base sm:text-lg text-secondary">{address.fullName}</h3>
                    {address.isDefault && (
                      <span className="px-2 sm:px-3 py-1 bg-[#F0F7F0] text-[#2E7D32] text-xs font-medium rounded-full">
                        Default
                      </span>
                    )}
                  </div>
                  <p className="text-sm sm:text-base text-[#6B635D] font-normal mb-1">{address.phone}</p>
                  <p className="text-sm font-normal text-secondary mb-1">{address.addressLine1}</p>
                  {address.addressLine2 && (
                    <p className="text-sm font-normal text-secondary mb-1">{address.addressLine2}</p>
                  )}
                  <p className="text-sm font-normal text-secondary">
                    {address.city}, {address.state} {address.postalCode}
                  </p>
                  <p className="text-sm font-normal text-secondary">{address.country}</p>
                </div>
                <div className="flex gap-3 sm:gap-4">
                  <button
                    onClick={() => handleOpenModal(address)}
                    className="text-sm font-normal text-secondary hover:text-[#D4A574]"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(address._id)}
                    className="text-sm font-normal text-[#FF383C] hover:text-red-600"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={() => handleOpenModal()}
        className="w-full sm:w-auto px-6 py-3 mt-6 bg-[#D4A574] text-white font-medium hover:bg-[#C49564] transition-colors duration-200 text-sm sm:text-base"
      >
        Add Address
      </button>

      {/* Address Modal */}
      <AddressModal
        isOpen={showModal}
        onClose={handleCloseModal}
        onSubmit={handleSubmitAddress}
        initialData={modalData}
        title={editingId ? "EDIT SHIPPING ADDRESS" : "ADD SHIPPING ADDRESS"}
      />
    </div>
  );
};

export default Addresses;